<template>
  <form @submit.prevent>
    <div>
      <h2>
        <span v-if="isEditMode">
          "{{ editedEntity.process.name | translate }}

          <span v-if="editedEntity.datasource">
            -
            {{ editedEntity.datasource.name | translate }}
          </span>

          - {{ editedEntity.datasubject.name | translate }}

          " {{ $t("common.update") }}</span
        >
        <span v-else>{{ $t("common.create") }}</span>
      </h2>

      <b-form-tags v-model="form.products" size="lg" class="mb-2" add-on-change no-outer-focus>
        <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
          <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :disabled="disabled || products.length === 0" :options="products">
            <template #first>
              <!-- This is required to prevent bugs with Safari -->
              <option disabled value=""> {{ $t("product.text") }}</option>
            </template>
          </b-form-select>

          <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
            <li v-for="tag in tags" :key="tag" class="list-inline-item">
              <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ tag }}</b-form-tag>
            </li>
          </ul>
        </template>
      </b-form-tags>

      <filter-select
        class="mt-4"
        id="receiverGroupFilter"
        ref="receiverGroupFilter"
        :placeholder="$t('receiver-group.text')"
        :multiple="!isEditMode"
        :isValid="!$v.form.receiverGroup.$invalid"
        :displayCount="3"
        @filter="onReceiverGroupSearch"
        :selected="form.receiverGroup"
        @changed="onReceiverGroupSelect"
        track="_id"
      />

      <filter-select
        class="mt-4"
        id="process-filter"
        ref="process-filter"
        :placeholder="$t('process.select')"
        :multiple="!isEditMode"
        :isValid="!$v.form.process.$invalid"
        :displayCount="3"
        @filter="onProcessSearch"
        :selected="form.process"
        @changed="processSelect"
        track="_id"
      />
      <filter-select
        class="mt-4"
        id="process-datasource-filter"
        ref="process-datasource-filter"
        :placeholder="$t('datasource.select')"
        :multiple="!isEditMode"
        :isValid="!$v.form.datasource.$invalid"
        :displayCount="3"
        @filter="onDatasourceSearch"
        :selected="form.datasource"
        @changed="datasourceSelect"
        track="_id"
      />

      <filter-select
        class="mt-4"
        id="datasubjectFilter"
        ref="datasubjectFilter"
        :multiple="!isEditMode"
        :selected="form.datasubject"
        @filter="onDatasubjectSearch"
        @changed="onDatasubjectSelect"
        placeholder="İlgili Kişi"
      />
      <filter-select
        class="mt-4"
        id="purposeFilter"
        ref="purposeFilter"
        :multiple="true"
        :selected="form.purposes"
        @filter="onProcessingPurposeSearch"
        @changed="onProcessingPurposeSelect"
        placeholder="İşleme/Aktarım Amacı"
      />
      <filter-select
        class="mt-4"
        id="legalReasonFilter"
        ref="legalReasonFilter"
        :multiple="true"
        :selected="form.legalReasons"
        @filter="onProcessingLegalReasonSearch"
        @changed="onProcessingLegalReasonSelect"
        placeholder="Hukuki Sebebi"
      />

      <br />
      <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-2">
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import SweetTextarea from "@c/form/sweet-textarea";
import { required } from "vuelidate/lib/validators";
const state = "processDatasourceDatasubjectTransferingLegal";

export default {
  components: { SweetTextarea },
  props: ["state"],
  data() {
    return {
      form: { products: [], receiverGroup: undefined, process: undefined, datasource: undefined, datasubject: undefined, purposes: [], legalReasons: [] },
    };
  },
  validations: {
    form: {
      receiverGroup: {
        required,
      },
      process: {
        required,
      },
      datasource: {},
      datasubject: {
        required,
      },
      purposes: {
        required,
      },
      legalReasons: {
        required,
      },
    },
  },
  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v && Object.keys(v).length > 0) {
          this.form = { ...v };
        } else {
          this.form = {};
        }
      },
    },
    "form.products": {
      immediate: true,
      deep: true,
      handler(n, o) {
        if (!!o && JSON.stringify(n) !== JSON.stringify(o)) {
          this.$refs["legalReasonFilter"]?.reset();
        }
      },
    },
  },
  computed: {
    isWorkspaceLayout() {
      return this.state === "workspaceProcessDatasourceDatasubjectTransferingLegal";
    },
    isUpdating() {
      return this.$store.getters[`${this.state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${this.state}/editedEntity`];
    },
    isEditMode() {
      return this.editedEntity && this.$store.getters[`${this.state}/isEditMode`];
    },
  },
  async mounted() {},
  methods: {
    async save() {
      const processes = this.isEditMode ? [this.form.process] : this.form.process;
      const datasources = this.isEditMode ? [this.form.datasource] : this.form.datasource;
      const datasubjects = this.isEditMode ? [this.form.datasubject] : this.form.datasubject;
      const receiverGroups = this.isEditMode ? [this.form.receiverGroup] : this.form.receiverGroup;
      this.$emit("onSave", this.form._id, this.form.products, receiverGroups, processes, datasources, datasubjects, this.form.purposes, this.form.legalReasons);
    },

    async onReceiverGroupSearch(value, callback) {
      const store = this.isWorkspaceLayout ? "workspaceReceiverGroup" : "managementReceiverGroup";
      const response = await this.$store.dispatch(`${store}/paginate`, {
        options: { limit: 200 },
        query: { name: { value }, ...(!this.isWorkspaceLayout && { "scope.value": { value: "system", isObject: true } }) },
      });

      callback(response.data?.docs ?? []);
    },
    onReceiverGroupSelect(item) {
      this.form = { ...this.form, receiverGroup: item };
    },

    async onProcessSearch(value, callback) {
      const store = this.isWorkspaceLayout ? "workspaceProcess" : "managementProcess";
      const response = await this.$store.dispatch(`${store}/paginate`, {
        options: { limit: 200 },
        query: { name: { value }, ...(!this.isWorkspaceLayout && { "scope.value": { value: "system", isObject: true } }) },
      });
      callback(response.data?.docs ?? []);
    },
    processSelect(item) {
      this.form = { ...this.form, process: item };
    },
    async onDatasourceSearch(value, callback) {
      const store = this.isWorkspaceLayout ? "workspaceDatasource" : "managementDatasource";
      const response = await this.$store.dispatch(`${store}/paginate`, {
        options: { limit: 200 },
        query: { name: { value }, ...(!this.isWorkspaceLayout && { "scope.value": { value: "system", isObject: true } }) },
      });
      callback(response.data?.docs ?? []);
    },
    datasourceSelect(item) {
      this.form = { ...this.form, datasource: item };
    },

    async onDatasubjectSearch(value, callback) {
      const store = this.isWorkspaceLayout ? "workspaceDatasubject" : "managementDatasubject";
      const response = await this.$store.dispatch(`${store}/paginate`, {
        options: { limit: 1000 },
        query: { name: { value }, ...(!this.isWorkspaceLayout && { "scope.value": { value: "system", isObject: true } }) },
      });
      callback(response.data?.docs ?? []);
    },
    onDatasubjectSelect(item) {
      this.form = { ...this.form, datasubject: item };
    },

    async onProcessingLegalReasonSearch(value, callback) {
      const query = {
        ...(!this.isWorkspaceLayout && { "scope.value": { value: "system", isObject: true } }),
        name: { value },
      };

      if (this.form.products?.length > 0) {
        query["$or"] = { value: this.form.products.map((i) => ({ products: i })), isObject: true };
      }
      const store = this.isWorkspaceLayout ? "workspaceLegalReason" : "managementLegalReason";
      const response = await this.$store.dispatch(`${store}/paginate`, {
        options: { limit: 1000 },
        query,
      });

      callback(response.data?.docs ?? []);
    },
    onProcessingLegalReasonSelect(items) {
      this.form = { ...this.form, legalReasons: items };
    },

    async onProcessingPurposeSearch(value, callback) {
      const store = this.isWorkspaceLayout ? "workspacePurpose" : "managementPurpose";
      const response = await this.$store.dispatch(`${store}/paginate`, {
        options: { limit: 1000 },
        query: { name: { value }, ...(!this.isWorkspaceLayout && { "scope.value": { value: "system", isObject: true } }) },
      });

      callback(response.data?.docs ?? []);
    },
    onProcessingPurposeSelect(items) {
      this.form = { ...this.form, purposes: items };
    },
  },
};
</script>
